/**
 * EPIC-119 D2/D4: which way the home team attacks, per period.
 *
 * Stored event coordinates are always canonical (home attacks toward x=120).
 * `home_attacks_right_first_half` (`null`/absent behaves as `true`) only
 * decides how that canonical frame is DISPLAYED: the pitch is mirrored when
 * the home team really attacked left, and periods 2 and 4 are the mirror of
 * periods 1 and 3 (extra-time period 3 uses the same side as period 1).
 */
import type { MatchStatus } from "../types";

/** True when the home team attacks LEFT in `period` (before any manual override). */
export const isHomeAttackingLeft = (
  homeAttacksRightFirstHalf: boolean | null | undefined,
  period: number,
): boolean => {
  const startsLeft = homeAttacksRightFirstHalf === false;
  const mirrorHalf = period === 2 || period === 4;
  return startsLeft !== mirrorHalf;
};

/**
 * The period the cockpit pitch should be oriented for. Unlike
 * `DEFAULT_PERIOD_MAP`, the halftime statuses already map to the NEXT
 * period, so ending a half flips the display without an operator click.
 */
const DISPLAY_PERIOD_BY_STATUS: Partial<Record<MatchStatus, number>> = {
  Live_First_Half: 1,
  Live_Second_Half: 2,
  Halftime: 2,
  Fulltime: 2,
  Completed: 2,
  Live_Extra_First: 3,
  Extra_Halftime: 4,
  Live_Extra_Second: 4,
};

export const displayPeriodForStatus = (
  status: MatchStatus | undefined,
): number => (status ? (DISPLAY_PERIOD_BY_STATUS[status] ?? 1) : 1);

/**
 * Final cockpit display flag: the real side XOR the manual "Voltear cancha"
 * override. Every `manualFieldFlip` reader (token positions, destination
 * un-flip per PMA-COCK-146, on-target edge) consumes this one value.
 */
export const computeFieldFlip = (
  homeAttacksRightFirstHalf: boolean | null | undefined,
  status: MatchStatus | undefined,
  manualOverride: boolean,
): boolean =>
  isHomeAttackingLeft(
    homeAttacksRightFirstHalf,
    displayPeriodForStatus(status),
  ) !== manualOverride;
